import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';

import { ApiError } from '../api/client';
import {
  createUser,
  deleteUser,
  listUsers,
  updateUser,
  type UserRecord,
  type UserRole,
} from '../api/users';

/**
 * Users admin page (T-079, arch §5.10).
 *
 * ADMIN-only allowlist management. The route guard (T-078) and the server's
 * `requireRole('ADMIN')` (T-069) are the real gates; this page assumes it is
 * only reached by an ADMIN and does not re-check the role itself.
 *
 * Responsibilities:
 *   - List users (paginated) with email / displayName / role / disabled.
 *   - An `entraObjectId === null` row is an INVITED user who has never signed
 *     in (ADR-011 state machine §6.3). We surface this as an explicit
 *     "invited — not yet signed in" badge so the admin understands why the
 *     row has no display name yet and why it is not "active".
 *   - Invite a user (email + role) → POST.
 *   - Per-row role change → PATCH `{role}`.
 *   - Disable / re-enable → PATCH `{disabled}`.
 *   - Soft-delete (confirm) → DELETE (server sets `disabled=true`; FK-safe).
 *
 * The two server 409s are surfaced INLINE, never as a thrown/toasted error,
 * and they are kept in separate regions because they originate from separate
 * actions and the admin needs to act on the right one:
 *   - Duplicate-email invite (`POST` → 409 `/errors/conflict`) → inline next
 *     to the invite form.
 *   - Last-admin guard (`PATCH`/`DELETE` → 409 `/errors/conflict`,
 *     "cannot remove the last administrator") → inline in the table's
 *     mutation-error region.
 *
 * Contract note (judgement call): the API (`apps/api/src/http/errors.ts`)
 * gives BOTH 409s the same `type: '/errors/conflict'`; they differ only by
 * `detail`. Rather than string-matching the detail to route them, we attach
 * each 409 to the mutation that raised it (invite vs. role/disable/delete) —
 * that is unambiguous and keeps the message next to the control the admin
 * used. We still render the server's `detail` verbatim so the exact reason
 * ("cannot remove the last administrator" / "a user with this email already
 * exists") is what the admin reads.
 *
 * Accessibility mirrors `CustomersListPage`: a captioned table with scoped
 * headers, `role="alert"` for errors, `role="status"` for loading,
 * `aria-label`led per-row controls, and `window.confirm` for the destructive
 * action (accessible by default — same rationale documented in
 * `CustomerEditPage`/`CustomersListPage`).
 */

const DEFAULT_PAGE_SIZE = 20;

const ROLES: readonly UserRole[] = ['ADMIN', 'STANDARD'];

function errorDetail(err: unknown): string {
  if (err instanceof ApiError) return err.detail;
  if (err instanceof Error) return err.message;
  return 'unknown error';
}

export function UsersAdminPage(): JSX.Element {
  const [page, setPage] = useState(1);
  const pageSize = DEFAULT_PAGE_SIZE;

  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['users', page, pageSize] as const,
    queryFn: () => listUsers({ page, pageSize }),
  });

  // --- Invite form state --------------------------------------------------
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<UserRole>('STANDARD');
  const [inviteError, setInviteError] = useState<string | null>(null);

  const inviteMutation = useMutation({
    mutationFn: () => createUser({ email: inviteEmail.trim(), role: inviteRole }),
    onSuccess: () => {
      setInviteEmail('');
      setInviteRole('STANDARD');
      setInviteError(null);
      void queryClient.invalidateQueries({ queryKey: ['users'] });
    },
    onError: (err) => {
      // 409 duplicate-email and every other failure land here as an inline
      // message under the form — never a toast/throw. ApiError.detail already
      // carries the server's reason ("a user with this email already
      // exists").
      setInviteError(errorDetail(err));
    },
  });

  function handleInvite(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    setInviteError(null);

    const email = inviteEmail.trim();
    if (email.length === 0) {
      setInviteError('Enter an email address to invite.');
      return;
    }
    inviteMutation.mutate();
  }

  // --- Row mutation error (shared region for role/disable/delete) ---------
  //
  // One region rather than per-row because at most one row mutation is in
  // flight at a time and the last-admin 409 is about the *set* of admins, not
  // a single row. Cleared whenever a new row mutation starts.
  const [rowError, setRowError] = useState<string | null>(null);

  const updateMutation = useMutation({
    mutationFn: (vars: { id: string; patch: { role?: UserRole; disabled?: boolean } }) =>
      updateUser(vars.id, vars.patch),
    onMutate: () => {
      setRowError(null);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['users'] });
    },
    onError: (err) => {
      // 409 "cannot remove the last administrator" (and any other failure)
      // surfaces here inline; the list is left untouched (no optimistic
      // update to roll back).
      setRowError(errorDetail(err));
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteUser(id),
    onMutate: () => {
      setRowError(null);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['users'] });
    },
    onError: (err) => {
      setRowError(errorDetail(err));
    },
  });

  function handleRoleChange(user: UserRecord, role: UserRole): void {
    if (role === user.role) return;
    updateMutation.mutate({ id: user.id, patch: { role } });
  }

  function handleToggleDisabled(user: UserRecord): void {
    updateMutation.mutate({ id: user.id, patch: { disabled: !user.disabled } });
  }

  function handleDelete(user: UserRecord): void {
    const confirmed = window.confirm(
      `Delete user "${user.email}"? They will be disabled and lose access immediately. ` +
        `This can be undone by re-enabling them.`,
    );
    if (!confirmed) return;
    deleteMutation.mutate(user.id);
  }

  const rowMutationPending = updateMutation.isPending || deleteMutation.isPending;

  const totalPages = query.data ? Math.max(1, Math.ceil(query.data.total / pageSize)) : 1;

  return (
    <section aria-label="Users" className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold">Users</h1>
      </header>

      <form
        onSubmit={handleInvite}
        aria-label="Invite user"
        className="space-y-3 max-w-xl border border-slate-200 bg-white p-4 rounded"
        noValidate
      >
        <h2 className="text-lg font-medium">Invite user</h2>

        {inviteError !== null && (
          <div id="invite-error" role="alert" className="text-sm text-red-700">
            {inviteError}
          </div>
        )}

        <div className="flex flex-col gap-1">
          <label htmlFor="invite-email" className="font-medium">
            Email
            <span aria-hidden="true" className="text-red-700">
              {' '}
              *
            </span>
          </label>
          <input
            id="invite-email"
            name="invite-email"
            type="email"
            value={inviteEmail}
            onChange={(e) => {
              setInviteEmail(e.target.value);
              if (inviteError !== null) setInviteError(null);
            }}
            aria-required="true"
            aria-invalid={inviteError !== null ? true : undefined}
            aria-describedby={inviteError !== null ? 'invite-error' : undefined}
            className="border rounded px-2 py-1"
          />
        </div>

        <div className="flex flex-col gap-1">
          <label htmlFor="invite-role" className="font-medium">
            Role
          </label>
          <select
            id="invite-role"
            name="invite-role"
            value={inviteRole}
            onChange={(e) => setInviteRole(e.target.value as UserRole)}
            className="border rounded px-2 py-1"
          >
            {ROLES.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>
        </div>

        <button
          type="submit"
          disabled={inviteMutation.isPending}
          className="bg-brand-teal text-white px-4 py-2 rounded disabled:opacity-50"
        >
          {inviteMutation.isPending ? 'Inviting…' : 'Invite user'}
        </button>
      </form>

      {query.isLoading && (
        <div role="status" aria-live="polite" aria-busy="true">
          Loading users…
        </div>
      )}

      {query.isError && (
        <div role="alert" className="text-red-700">
          Failed to load users:{' '}
          {query.error instanceof Error ? query.error.message : 'unknown'}
        </div>
      )}

      {rowError !== null && (
        <div role="alert" className="text-red-700">
          {rowError}
        </div>
      )}

      {query.data && (
        <>
          <table className="min-w-full border border-slate-200 bg-white">
            <caption className="sr-only">Users</caption>
            <thead className="bg-slate-100">
              <tr>
                <th scope="col" className="text-left p-2">
                  Email
                </th>
                <th scope="col" className="text-left p-2">
                  Display name
                </th>
                <th scope="col" className="text-left p-2">
                  Role
                </th>
                <th scope="col" className="text-left p-2">
                  Status
                </th>
                <th scope="col" className="text-left p-2">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {query.data.items.length === 0 && (
                <tr>
                  <td colSpan={5} className="p-4 text-center text-slate-500">
                    No users yet.
                  </td>
                </tr>
              )}
              {query.data.items.map((user) => {
                const invited = user.entraObjectId === null;
                return (
                  <tr key={user.id} className="border-t border-slate-200">
                    <td className="p-2">{user.email}</td>
                    <td className="p-2">{user.displayName ?? ''}</td>
                    <td className="p-2">
                      <label
                        htmlFor={`role-${user.id}`}
                        className="sr-only"
                      >{`Role for ${user.email}`}</label>
                      <select
                        id={`role-${user.id}`}
                        value={user.role}
                        onChange={(e) => handleRoleChange(user, e.target.value as UserRole)}
                        disabled={rowMutationPending}
                        className="border rounded px-2 py-1 disabled:opacity-50"
                      >
                        {ROLES.map((r) => (
                          <option key={r} value={r}>
                            {r}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="p-2">
                      {user.disabled ? (
                        <span className="inline-flex rounded px-2 py-0.5 text-xs font-medium bg-red-100 text-red-800">
                          Disabled
                        </span>
                      ) : invited ? (
                        <span className="inline-flex rounded px-2 py-0.5 text-xs font-medium bg-yellow-100 text-yellow-800">
                          Invited — not yet signed in
                        </span>
                      ) : (
                        <span className="inline-flex rounded px-2 py-0.5 text-xs font-medium bg-green-100 text-green-800">
                          Active
                        </span>
                      )}
                    </td>
                    <td className="p-2 space-x-2 whitespace-nowrap">
                      <button
                        type="button"
                        onClick={() => handleToggleDisabled(user)}
                        disabled={rowMutationPending}
                        className="text-brand-teal underline disabled:opacity-50"
                        aria-label={`${user.disabled ? 'Re-enable' : 'Disable'} ${user.email}`}
                      >
                        {user.disabled ? 'Re-enable' : 'Disable'}
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDelete(user)}
                        disabled={rowMutationPending}
                        className="text-red-700 underline disabled:opacity-50"
                        aria-label={`Delete ${user.email}`}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          <nav aria-label="Pagination" className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="px-3 py-1 border rounded disabled:opacity-50"
            >
              Previous
            </button>
            <span>
              Page {page} of {totalPages}
            </span>
            <button
              type="button"
              onClick={() => setPage((p) => (p < totalPages ? p + 1 : p))}
              disabled={page >= totalPages}
              className="px-3 py-1 border rounded disabled:opacity-50"
            >
              Next
            </button>
          </nav>
        </>
      )}
    </section>
  );
}
